<template>
  <TextBase
    :is="componentType"
    :type="componentType === 'button' ? 'button' : undefined"
    :href="props.href ? props.href : undefined"
    :target="target"
    :rel="rel"
    :disabled="props.button || props.href ? props.disabled : undefined"
    class="inline-flex items-center font-medium border-1 border text-left"
    font="font-medium"
    :color="textColor"
    :size="textSize"
    :class="[
      classes,
      props.button && !props.disabled && buttonBackgroundHoverColor,
      props.disabled && 'opacity-50',
      props.href && 'hover:underline',
      props.square ? 'rounded-md' : 'rounded-full',
      props.pending && 'opacity-80 border-dashed',
    ]"
    :dark="darkTextColor"
  >
    <svg
      v-if="props.selected === false"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="-ml-1 mr-1"
      :class="[
        iconSizeClasses(buttonSizeToIconSizeMap(props.size)),
        selectIconStrokeColor,
      ]"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M9 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <HeroiconContainer
      v-else-if="props.selected === true"
      class="-ml-1 mr-1"
      :icon="CheckCircleIcon"
      :color="null"
      :size="buttonSizeToIconSizeMap(props.size)"
    />
    <HeroiconContainer
      v-else-if="props.leadingIcon"
      class="-ml-1 mr-1"
      :icon="props.leadingIcon"
      :color="null"
      :size="buttonSizeToIconSizeMap(props.size)"
    />

    <slot />
    <HeroiconContainer
      v-if="props.trailingIcon"
      class="ml-1"
      :icon="props.trailingIcon"
      :color="null"
      :size="buttonSizeToIconSizeMap(props.size)"
    />
  </TextBase>
</template>

<script setup>
import TextBase from '@/Common/Text/TextBase.vue'
import { predefinedColors } from '@/Common/util/tailwind/predefinedColors'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import { Link } from '@inertiajs/vue3'
import { computed } from 'vue'
import HeroiconContainer from '../HeroiconContainer.vue'
import { buttonSizeToIconSizeMap, iconSizeClasses } from '../common'

const props = defineProps({
  color: {
    type: String,
    default: 'gray',
  },
  size: {
    type: String,
    default: 'sm',
  },
  square: Boolean,
  leadingIcon: Function,
  trailingIcon: Function,
  button: Boolean,
  disabled: Boolean,
  darkBorder: Boolean,
  pending: Boolean,
  selected: {
    type: Boolean,
    default: null,
  },
  href: String,
  external: Boolean,
})

const componentType = computed(() => {
  if (props.external) return 'a'

  return props.href && !props.disabled ? Link : props.button ? 'button' : 'span'
})

const target = computed(() =>
  props.external && !props.noBlank ? '_blank' : undefined
)
const rel = computed(() => (props.external ? 'noopener noreferrer' : undefined))

const textColor = computed(() => predefinedColors[props.color].textColor)

const darkTextColor = computed(
  () => predefinedColors[props.color].darkTextColor
)
const backgroundColor = computed(
  () => predefinedColors[props.color].backgroundColor
)
const borderColor = computed(() =>
  props.darkBorder || props.pending
    ? predefinedColors[props.color].borderColorWithoutOpacity
    : predefinedColors[props.color].borderColor
)

const sizeClasses = computed(() => {
  return {
    xs: 'px-[calc(0.375rem-1px)] py-[calc(0.125rem-1px)]',
    sm: 'px-[calc(0.5rem-1px)] py-[calc(0.25rem-1px)]',
    lg: 'px-[calc(0.75rem-1px)] py-[calc(0.25rem-1px)]',
  }[props.size]
})

const classes = computed(() =>
  [backgroundColor.value, borderColor.value, sizeClasses.value].join(' ')
)

const textSize = computed(() => {
  return {
    xs: 'text-xs',
    sm: 'text-xs',
    lg: 'text-sm',
  }[props.size]
})

const buttonBackgroundHoverColor = computed(
  () => predefinedColors[props.color].removeButtonBackgroundHoverColor
)

const selectIconStrokeColor = computed(
  () => predefinedColors[props.color].removeButtonStrokeColor
)
</script>
